import { Check, Pencil, X } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";
import { comoEstaLaCuenta } from "@/lib/voucher";
import { fecha as comoFecha, dinero, RAYA, type Voucher } from "./comun";

// ===========================================================================
// LO QUE DICE EL VOUCHER, Y LA FORMA DE CORREGIRLO SIN SALIR DE AQUÍ.
//
// El cuadro de la revisión guiada solo enseña. Aquí además se escribe, porque
// es donde el dato estorba: contabilidad estaba mirando un voucher del BCP que
// dice «$300.00» y debajo «Total cobrado 303.18», el lector se quedó con el
// segundo, y para arreglar tres dígitos había que irse a otra pantalla.
//
// CUATRO CAMPOS Y NO MÁS: importe, moneda, fecha y número de operación. Son
// los que el sistema sabe guardar corregidos —la corrección va por imagen, en
// `verificacion_voucher`, y vale para todos los expedientes donde esa foto
// aparece—. El resto se enseña y no se toca: inventar un campo editable que
// después no se guarda es peor que no ofrecerlo.
//
// Y TODA CORRECCIÓN DEJA FIRMA. Un dato que alguien cambió y un dato que salió
// del lector se ven igual si no se dice, y seis meses después nadie puede
// explicar por qué el importe no es el que imprime el papel.
// ===========================================================================

/** Lo que viaja al servidor. Van los cuatro juntos, siempre. */
export type Correccion = {
  importe: number | null;
  moneda: "PEN" | "USD" | null;
  fecha: string | null;
  operacion: string | null;
};

type Campo = "importe" | "fecha" | "operacion";

export function DiceElVoucher({
  voucher,
  guardando,
  alCorregir,
}: {
  voucher: Voucher;
  guardando: boolean;
  alCorregir: (c: Correccion) => void;
}) {
  const [editando, setEditando] = useState<Campo | null>(null);
  const [borrador, setBorrador] = useState("");
  const [moneda, setMoneda] = useState<"PEN" | "USD">(voucher.moneda ?? "PEN");

  /** El estado actual, que es la base sobre la que se escribe un campo. */
  const ahora: Correccion = {
    importe: voucher.importe,
    moneda: voucher.moneda,
    fecha: voucher.fechaPago,
    operacion: voucher.operacion,
  };

  const abrir = (campo: Campo) => {
    setEditando(campo);
    setMoneda(voucher.moneda ?? "PEN");
    setBorrador(
      campo === "importe"
        ? voucher.importe === null
          ? ""
          : (voucher.importe / 100).toFixed(2)
        : campo === "fecha"
          ? (voucher.fechaPago ?? "")
          : (voucher.operacion ?? ""),
    );
  };

  const guardar = () => {
    if (!editando) return;
    const limpio = borrador.trim();

    if (editando === "importe") {
      // Se admite «1,234.56» y «1234.56»: la coma es separador de miles en
      // este país y quien escribe un importe la pone sin pensarlo.
      const n = Number(limpio.replace(/,/g, ""));
      if (limpio && (!Number.isFinite(n) || n < 0)) return;
      alCorregir({
        ...ahora,
        moneda,
        importe: limpio ? Math.round(n * 100) : null,
      });
    } else if (editando === "fecha") {
      alCorregir({ ...ahora, fecha: limpio || null });
    } else {
      alCorregir({ ...ahora, operacion: limpio || null });
    }
    setEditando(null);
  };

  const cuenta = comoEstaLaCuenta({
    cuentaDestino: voucher.cuentaDestino,
    cuentaRegistrada: voucher.cuentaDelGrupo
      ? {
          banco: voucher.cuentaDelGrupo.banco,
          numero: voucher.cuentaDelGrupo.numero,
          moneda: voucher.cuentaDelGrupo.moneda === "USD" ? "USD" : "PEN",
          razonSocial: voucher.cuentaDelGrupo.razonSocial,
        }
      : null,
  });

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
          Dice el voucher
        </span>
        {voucher.medio ? <Chip acento>{voucher.medio}</Chip> : null}
        {voucher.origenImagen ? <Chip>{voucher.origenImagen}</Chip> : null}
        {voucher.correccion?.veredicto === "corregido" ? (
          <Chip tono="ok">Corregido a mano</Chip>
        ) : null}
      </div>

      {/* --- el importe, que es lo que más se corrige ------------------- */}
      {editando === "importe" ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            value={moneda}
            onChange={(e) => setMoneda(e.target.value as "PEN" | "USD")}
            className="h-9 rounded-md border bg-background px-2 text-[13px] outline-none"
            aria-label="Moneda"
          >
            <option value="PEN">S/</option>
            <option value="USD">US$</option>
          </select>
          <input
            // El foco va al campo que se acaba de pulsar. Con un `ref` y no
            // con `autoFocus`: el atributo mueve el foco también cuando el
            // navegador restaura la página, y ahí nadie ha pulsado nada.
            ref={(el) => el?.focus()}
            value={borrador}
            onChange={(e) => setBorrador(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") guardar();
              if (e.key === "Escape") setEditando(null);
            }}
            inputMode="decimal"
            aria-label="Importe del voucher"
            className="h-9 w-40 rounded-md border bg-background px-2 font-semibold text-[19px] tabular-nums outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <Botones
            guardando={guardando}
            alGuardar={guardar}
            alCancelar={() => setEditando(null)}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => abrir("importe")}
          className="group mt-1 -ml-1 flex items-baseline gap-2 rounded px-1 py-0.5 text-left hover:bg-muted"
        >
          <span className="font-bold text-3xl tabular-nums tracking-tight">
            {dinero(voucher.importe, voucher.moneda)}
          </span>
          <Pencil className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </button>
      )}

      {voucher.interbancaria ? (
        <p className="mt-2 rounded bg-amber-500/15 px-2 py-1 text-[11.5px] text-amber-700 dark:text-amber-400">
          Es interbancaria: el dinero cambia de banco y puede abonarse hasta dos
          días después. La ventana ya los tiene sumados.
        </p>
      ) : null}

      <dl className="mt-4 space-y-1">
        <Fila
          clave="Fecha del pago"
          editando={editando === "fecha"}
          valor={
            voucher.fechaPago
              ? `${comoFecha(voucher.fechaPago)}${voucher.hora ? ` · ${voucher.hora}` : ""}`
              : null
          }
          vacioDice="el lector no la sacó · escribila"
          alAbrir={() => abrir("fecha")}
        >
          <input
            ref={(el) => el?.focus()}
            type="date"
            value={borrador}
            onChange={(e) => setBorrador(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") guardar();
              if (e.key === "Escape") setEditando(null);
            }}
            aria-label="Fecha del pago"
            className="h-8 rounded-md border bg-background px-2 text-[12.5px] tabular-nums outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <Botones
            guardando={guardando}
            alGuardar={guardar}
            alCancelar={() => setEditando(null)}
          />
        </Fila>

        <Fila
          clave="N.º de operación"
          editando={editando === "operacion"}
          valor={voucher.operacion}
          mono
          vacioDice="sin número · escribilo"
          alAbrir={() => abrir("operacion")}
        >
          <input
            ref={(el) => el?.focus()}
            value={borrador}
            onChange={(e) => setBorrador(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") guardar();
              if (e.key === "Escape") setEditando(null);
            }}
            aria-label="Número de operación"
            className="h-8 w-44 rounded-md border bg-background px-2 font-mono text-[12.5px] outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <Botones
            guardando={guardando}
            alGuardar={guardar}
            alCancelar={() => setEditando(null)}
          />
        </Fila>

        {/* Lo que se enseña y no se toca: el sistema no sabe guardarlo
            corregido, y un campo que invita a escribir y luego lo pierde es
            peor que no ofrecerlo. */}
        <Fila clave="Cómo se pagó" valor={voucher.medio} />
        <Fila clave="Banco del voucher" valor={voucher.banco} />
        <Fila clave="Tipo de imagen" valor={voucher.origenImagen} />
        <Fila clave="Lo pagó" valor={voucher.pagador} />
        <Fila clave="A nombre de" valor={voucher.beneficiario} />
        <Fila
          clave="Cuenta de destino"
          mono
          valor={voucher.cuentaDestino}
          vacioDice={
            voucher.cuentaDelGrupo
              ? `${voucher.cuentaDelGrupo.numero} · deducida, no está en el papel`
              : null
          }
        />
      </dl>

      {cuenta ? (
        <p
          className={cn(
            "mt-3 rounded px-2 py-1.5 text-[11.5px]",
            cuenta.tono === "ok"
              ? "bg-emerald-600/10 text-emerald-700 dark:text-emerald-400"
              : "bg-amber-500/15 text-amber-700 dark:text-amber-400",
          )}
        >
          {cuenta.texto}
        </p>
      ) : null}

      {/* --- de quién es cada dato ------------------------------------- */}
      <p className="mt-3 border-t pt-2 text-[11px] text-muted-foreground">
        {voucher.correccion?.veredicto === "corregido" ? (
          <>
            Lo corrigió{" "}
            <b className="text-foreground">
              {voucher.correccion.quien ?? "alguien"}
            </b>{" "}
            el {comoFecha(voucher.correccion.momento.slice(0, 10))}. Lo escrito
            a mano manda sobre el lector y no se pierde al volver a leerlo.
          </>
        ) : voucher.correccion ? (
          <>
            Revisado por{" "}
            <b className="text-foreground">
              {voucher.correccion.quien ?? "alguien"}
            </b>{" "}
            el {comoFecha(voucher.correccion.momento.slice(0, 10))} ·{" "}
            {voucher.correccion.veredicto === "correcto"
              ? "dio por bueno lo leído"
              : voucher.correccion.veredicto === "ilegible"
                ? "lo marcó ilegible"
                : "dijo que no es un voucher"}
          </>
        ) : (
          <>
            Todo esto lo leyó la máquina. Pulsá cualquier dato para corregirlo.
          </>
        )}
      </p>
    </div>
  );
}

function Fila({
  clave,
  valor,
  vacioDice,
  mono,
  editando,
  alAbrir,
  children,
}: {
  clave: string;
  valor: string | null;
  /** Qué poner cuando no hay valor. Sin esto, una raya. */
  vacioDice?: string | null;
  mono?: boolean;
  editando?: boolean;
  alAbrir?: () => void;
  children?: React.ReactNode;
}) {
  if (editando) {
    return (
      <div className="rounded-md bg-muted/60 px-2 py-2">
        <dt className="mb-1 font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
          {clave}
        </dt>
        <dd className="flex flex-wrap items-center gap-2">{children}</dd>
      </div>
    );
  }

  const falta = !valor;
  const contenido = valor ?? vacioDice ?? RAYA;

  const cuerpo = (
    <>
      <dt className="shrink-0 text-muted-foreground">{clave}</dt>
      <dd
        className={cn(
          "truncate text-right font-medium",
          mono && "font-mono text-[12px]",
          // Lo que falta y se puede escribir se ve: es el trabajo que queda.
          falta && alAbrir && "text-amber-700 dark:text-amber-400",
          falta && !alAbrir && "text-muted-foreground",
        )}
        title={contenido}
      >
        {contenido}
      </dd>
    </>
  );

  if (!alAbrir) {
    return (
      <div className="flex items-baseline justify-between gap-4 px-2 py-1 text-[12.5px]">
        {cuerpo}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={alAbrir}
      className="group flex w-full items-baseline justify-between gap-4 rounded-md px-2 py-1 text-left text-[12.5px] hover:bg-muted"
    >
      {cuerpo}
      <Pencil className="size-3 shrink-0 self-center text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
}

function Botones({
  guardando,
  alGuardar,
  alCancelar,
}: {
  guardando: boolean;
  alGuardar: () => void;
  alCancelar: () => void;
}) {
  return (
    <span className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={alGuardar}
        disabled={guardando}
        className="inline-flex h-8 items-center gap-1.5 rounded-md bg-foreground px-3 font-semibold text-[12px] text-background disabled:opacity-60"
      >
        <Check className="size-3.5" />
        Guardar
      </button>
      <button
        type="button"
        onClick={alCancelar}
        className="inline-flex h-8 items-center rounded-md border px-2.5 text-[12px] text-muted-foreground hover:bg-muted"
      >
        <X className="size-3.5" />
      </button>
    </span>
  );
}

function Chip({
  children,
  acento,
  tono,
}: {
  children: React.ReactNode;
  acento?: boolean;
  tono?: "ok";
}) {
  return (
    <span
      className={cn(
        "rounded-full border px-2 py-0.5 font-medium text-[10.5px] uppercase tracking-wide",
        tono === "ok"
          ? "border-emerald-600/40 bg-emerald-600/10 text-emerald-700 dark:text-emerald-400"
          : acento
            ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-700 dark:text-cyan-400"
            : "text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}
